import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Users, UserPlus, Search, Send, ChevronLeft, Settings } from "lucide-react";
import { useFriends } from "@/hooks/useFriends";
import { FriendsTab } from "@/components/friends/FriendsTab";
import { RequestsTab } from "@/components/friends/RequestsTab";
import { SearchTab } from "@/components/friends/SearchTab";
import { InviteTab } from "@/components/friends/InviteTab";
import { ManagedTimelinesTab } from "@/components/friends/ManagedTimelinesTab";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

interface MobileNavItem {
  key: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  component: React.ComponentType;
  count?: number;
}

export const FriendsMobileNav = () => {
  const { friends, pendingRequests, loading } = useFriends();
  const { t } = useTranslation('friends');
  const [searchParams] = useSearchParams();
  const [activeSheet, setActiveSheet] = useState<string | null>(null);
  const fromInvite = searchParams.get('from') === 'invite';

  // Open specific tab if specified in URL
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'managed') {
      setActiveSheet('managed');
    } else if (tab === 'requests') {
      setActiveSheet('requests');
      if (fromInvite) {
        toast.success('Welcome! Here are your pending friend requests from invitations.');
      }
    }
  }, [searchParams, fromInvite]);

  const navItems: MobileNavItem[] = [
    {
      key: "friends",
      title: t('tabs.friends'),
      description: t('mobile.friends.description', 'Manage your friends list'),
      icon: Users,
      component: FriendsTab,
      count: loading ? undefined : friends.length
    },
    {
      key: "requests",
      title: t('tabs.requests'),
      description: t('mobile.requests.description', 'View pending friend requests'),
      icon: UserPlus,
      component: RequestsTab,
      count: loading ? undefined : pendingRequests.length
    },
    {
      key: "search",
      title: t('tabs.search'),
      description: t('mobile.search.description', 'Find and add new friends'),
      icon: Search,
      component: SearchTab
    },
    {
      key: "invite",
      title: t('tabs.invite'),
      description: t('mobile.invite.description', 'Invite friends to join'),
      icon: Send,
      component: InviteTab
    },
    {
      key: "managed",
      title: "Managed Timelines",
      description: "Manage timelines you've created for others",
      icon: Settings,
      component: ManagedTimelinesTab
    }
  ];

  const renderSheetContent = (item: MobileNavItem) => {
    const Component = item.component;
    return (
      <Sheet open={activeSheet === item.key} onOpenChange={(open) => setActiveSheet(open ? item.key : null)}>
        <SheetTrigger asChild>
          <Card className="uplivion-card cursor-pointer hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-uplivion-blue/10">
                    <item.icon className="w-6 h-6 text-uplivion-blue" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      {item.title}
                      {item.count !== undefined && (
                        <span className="text-sm font-normal text-muted-foreground">
                          ({item.count})
                        </span>
                      )}
                    </h3>
                    <p className="text-muted-foreground text-sm">{item.description}</p>
                  </div>
                </div>
                <ChevronLeft className="w-5 h-5 text-muted-foreground rotate-180" />
              </div>
            </CardContent>
          </Card>
        </SheetTrigger>
        <SheetContent side="right" className="w-full p-0 flex flex-col">
          <SheetHeader className="p-6 border-b bg-background/95 backdrop-blur sticky top-0 z-10">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveSheet(null)}
                className="p-1 h-8 w-8"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <SheetTitle className="text-lg font-semibold">{item.title}</SheetTitle>
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-auto p-6">
            <Component />
          </div>
        </SheetContent>
      </Sheet>
    );
  };

  return (
    <div className="space-y-4">
      {navItems.map((item) => (
        <div key={item.key}>
          {renderSheetContent(item)}
        </div>
      ))}
    </div>
  );
};